/**
 * How long a group or broadcast-list name may be, in characters.
 *
 * One constant because the screens that create a name and the screens that
 * edit it have to agree, and they did not: creating allowed 100 while both
 * edit pages declared 50. Open an existing 100-character broadcast and the
 * edit panel showed "100/50" — a counter contradicting its own stated maximum,
 * over a value the save path then accepted anyway, because the only length
 * check there was a minimum of 2.
 *
 * 100 rather than 50 because 100 is the limit names are actually created
 * under, so it is the shortest limit that can represent every name the UI can
 * produce. Lowering it to 50 would strand existing names instead.
 *
 * The server is looser still — `sdk-server` broadcasts use `NAME_MAX = 200` —
 * so this is the tighter UI rule, and staying under it is always valid.
 *
 * `maxLength` on an input is not enough on its own: it constrains typing and
 * pasting, never a value set programmatically, so an over-long existing name
 * loads into the field untouched. Anything seeding a field from stored data
 * must also validate on save.
 */
export const CONVERSATION_NAME_MAX = 100

/** Shortest usable name — a single character is almost always a slip. */
export const CONVERSATION_NAME_MIN = 2

/**
 * Strips newlines out of a name field's value.
 *
 * The name box is one (wrapped) line, and Enter is blocked from inserting a
 * line break into it so typing one in is impossible. Pasting is not covered
 * by that block: a value assigned by the paste event can still carry a
 * `\n`, which split the rendered name across two visual lines in a field
 * that otherwise never shows more than one. Flattening every line break to
 * a single space keeps a multi-line clipboard paste as one readable name
 * instead of silently dropping line breaks and running words together.
 */
export function flattenPastedName(value: string): string {
  return value.replace(/\r\n|\r|\n/g, " ")
}
